import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Bot, Stethoscope, Activity, Footprints, Scan, Microscope, HeartPulse, Sparkles, ExternalLink as ExtLinkIcon, MapPin, Clock, Phone, ExternalLink, CheckCircle2, Calculator, Ruler, Syringe, Droplet } from "lucide-react";
import { dict } from "../i18n";
import { fetchContent } from "../lib/api";
import SEO from "../components/SEO";
import { getSectionByServiceId, pricingSectionUrl } from "../lib/pricingSections";
import { pagePath } from "../lib/routes";

const ICONS = { stethoscope: Stethoscope, activity: Activity, footprints: Footprints, scan: Scan, microscope: Microscope, "heart-pulse": HeartPulse, sparkles: Sparkles };

// Hero image served as two variants so mobile 3G/4G users don't pay for a
// desktop-sized asset. Preloaded per-viewport in public/index.html.
const HERO_IMG_MOBILE = "/hero-mobile.webp";   // 640×478, ~30KB
const HERO_IMG_DESKTOP = "/hero-desktop.webp"; // 900×672, ~49KB

// ─────────────────────────────────────────────────────────────────────────
// Auto-rotating teaser carousel for the 4 calculators
// ─────────────────────────────────────────────────────────────────────────
function CalculatorCarousel({ lang }) {
  const slides = [
    {
      key: "hba1c",
      Icon: Activity,
      iconBg: "bg-sapphire-50",
      iconColor: "text-sapphire-700",
      accentBorder: "border-sapphire-300",
      accentDot: "bg-sapphire-700",
      title: lang === "et" ? "HbA1c kalkulaator" : lang === "ru" ? "Калькулятор HbA1c" : "HbA1c calculator",
      desc:
        lang === "et"
          ? "% ↔ mmol/mol ↔ keskmine veresuhkur. Vaata, kus oled diabeedi kontrolli skaalal."
          : lang === "ru"
          ? "% ↔ mmol/mol ↔ средняя глюкоза. Узнайте, где вы по шкале контроля диабета."
          : "% ↔ mmol/mol ↔ average glucose. See where you stand on the diabetes control scale.",
    },
    {
      key: "bmi",
      Icon: Ruler,
      iconBg: "bg-teal-200/40",
      iconColor: "text-teal-500",
      accentBorder: "border-teal-300",
      accentDot: "bg-teal-500",
      title: lang === "et" ? "KMI + vööümbermõõt" : lang === "ru" ? "ИМТ + талия" : "BMI + waist",
      desc:
        lang === "et"
          ? "Arvuta kehamassiindeks ja hinda kõhupiirkonna rasvumise riski WHO standardite järgi."
          : lang === "ru"
          ? "Рассчитайте ИМТ и оцените риск ожирения по стандартам ВОЗ."
          : "Calculate your BMI and assess waist-related obesity risk by WHO standards.",
    },
    {
      key: "icr",
      Icon: Syringe,
      iconBg: "bg-sapphire-50",
      iconColor: "text-sapphire-700",
      accentBorder: "border-sapphire-300",
      accentDot: "bg-sapphire-700",
      title:
        lang === "et"
          ? "ICR — insuliini-süsivesikute suhe"
          : lang === "ru"
          ? "ICR — соотношение инсулин/углеводы"
          : "ICR — insulin-to-carb ratio",
      desc:
        lang === "et"
          ? "1 TÜ kiiretoimelist insuliini iga X g süsivesiku kohta. Arvuta söögikorra boolus."
          : lang === "ru"
          ? "1 ед быстрого инсулина на X г углеводов. Рассчитайте болюс на приём пищи."
          : "1 unit of rapid insulin per X g of carbs. Calculate your meal bolus.",
    },
    {
      key: "isf",
      Icon: Droplet,
      iconBg: "bg-teal-200/40",
      iconColor: "text-teal-500",
      accentBorder: "border-teal-300",
      accentDot: "bg-teal-500",
      title:
        lang === "et"
          ? "ISF — insuliini tundlikkuse tegur"
          : lang === "ru"
          ? "ISF — фактор чувствительности к инсулину"
          : "ISF — insulin sensitivity factor",
      desc:
        lang === "et"
          ? "1 TÜ langetab veresuhkrut X mmol/L võrra. Arvuta korrektsiooniannus."
          : lang === "ru"
          ? "1 ед снижает глюкозу на X mmol/L. Рассчитайте коррекцию."
          : "1 unit lowers BG by X mmol/L. Calculate your correction dose.",
    },
  ];

  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (paused) return undefined;
    const id = setInterval(() => setActive((i) => (i + 1) % slides.length), 4000);
    return () => clearInterval(id);
  }, [paused, slides.length]);

  return (
    <div
      className="relative"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      data-testid="home-calc-carousel"
    >
      <div className="relative h-[230px] sm:h-[210px]">
        {slides.map((s, idx) => {
          const Icon = s.Icon;
          const isActive = idx === active;
          return (
            <Link
              key={s.key}
              to={pagePath("calculators", lang)}
              data-testid={`home-calc-slide-${s.key}`}
              aria-hidden={!isActive}
              tabIndex={isActive ? 0 : -1}
              className={`absolute inset-0 card-clinic !p-6 flex flex-col justify-between transition-all duration-500 ease-out group hover:shadow-ambientLg ${
                isActive ? "opacity-100 translate-y-0 z-10" : "opacity-0 translate-y-2 pointer-events-none"
              } ${s.accentBorder}`}
            >
              <div>
                <div className={`h-12 w-12 rounded-xl ${s.iconBg} ${s.iconColor} flex items-center justify-center mb-4`}>
                  <Icon className="h-6 w-6" />
                </div>
                <div className="text-[18px] font-semibold text-surface-ink leading-snug">
                  {s.title}
                </div>
                <p className="text-[13px] text-surface-inkSoft mt-2 leading-relaxed">
                  {s.desc}
                </p>
              </div>
              <div className={`text-[12px] font-medium ${s.iconColor} inline-flex items-center gap-1.5 group-hover:gap-2.5 transition-all mt-3`}>
                {lang === "et" ? "Ava" : lang === "ru" ? "Открыть" : "Open"}
                <ArrowRight className="h-3.5 w-3.5" />
              </div>
            </Link>
          );
        })}
      </div>

      <div className="mt-4 flex items-center gap-2" role="tablist">
        {slides.map((s, idx) => (
          <button
            key={s.key}
            type="button"
            role="tab"
            aria-selected={idx === active}
            aria-label={s.title}
            onClick={() => setActive(idx)}
            data-testid={`home-calc-dot-${s.key}`}
            className={`h-1.5 rounded-full transition-all ${
              idx === active ? `w-8 ${s.accentDot}` : "w-4 bg-surface-outline hover:bg-surface-inkSoft/60"
            }`}
          />
        ))}
      </div>
    </div>
  );
}

export default function Home({ lang }) {
  const t = dict[lang];
  const [clinic, setClinic] = useState(null);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    let alive = true;
    setLoadError(false);
    fetchContent()
      .then((data) => { if (alive) { setClinic(data); setLoadError(false); } })
      .catch(() => { if (alive) setLoadError(true); });
    return () => { alive = false; };
  }, []);

  const retryLoad = () => {
    setLoadError(false);
    fetchContent().then(setClinic).catch(() => setLoadError(true));
  };

  return (
    <>
      <SEO lang={lang} page="home" />

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-sapphire-50 to-white pointer-events-none" />
        <div className="absolute inset-0 grain opacity-50 pointer-events-none" />
        <div className="container-page relative grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 py-10 lg:py-14 items-center">
          <div className="animate-fade-up">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold text-surface-ink tracking-tight leading-[1.05]">
              {t.hero.title}
            </h1>
            <p className="mt-5 text-lg text-surface-inkSoft max-w-xl leading-relaxed">{t.hero.subtitle}</p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <button
                onClick={() => document.querySelector("[data-testid='ai-fab']")?.click()}
                data-testid="hero-ai-btn"
                className="btn-primary"
              >
                <Bot className="h-5 w-5" />
                {t.hero.ctaAI}
              </button>
              <a
                href="https://veebiregistratuur.ee/tallinna-endokrinoloogia-kliinik/"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-book-btn"
                className="btn-ghost"
              >
                {t.hero.ctaBook}
                <ArrowRight className="h-4 w-4" />
              </a>
            </div>
            <div className="mt-8 flex flex-wrap gap-x-6 gap-y-3 text-[13px] text-surface-inkSoft">
              <span className="inline-flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-teal-500" /> {lang === "et" ? "Tervisekassaga 20€/5€" : lang === "ru" ? "С медстраховкой 20€/5€" : "With insurance 20€/5€"}</span>
              <span className="inline-flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-teal-500" /> {lang === "et" ? "Tervisekassata ja saatekirjata tasulised vastuvõtud" : lang === "ru" ? "Без медстраховки и направления — платные приёмы" : "Without insurance, no referral — paid appointments"}</span>
              <span className="inline-flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-teal-500" /> {lang === "et" ? "Tallinn ja Pärnu" : lang === "ru" ? "Таллинн и Пярну" : "Tallinn & Pärnu"}</span>
            </div>
          </div>

          <div className="relative animate-fade-up [animation-delay:120ms]">
            <div className="aspect-[4/3] rounded-3xl overflow-hidden border border-surface-outline/40 shadow-ambientLg">
              <picture>
                <source media="(max-width: 640px)" srcSet={HERO_IMG_MOBILE} type="image/webp" />
                <img
                  src={HERO_IMG_DESKTOP}
                  alt={lang === "et" ? "Endokrinoloogia kliinik Tallinnas" : lang === "ru" ? "Клиника эндокринологии в Таллинне" : "Endocrinology clinic in Tallinn"}
                  className="w-full h-full object-cover"
                  width="900"
                  height="672"
                  fetchPriority="high"
                  decoding="async"
                />
              </picture>
            </div>
            <div className="absolute -bottom-5 -left-5 sm:-left-8 glass-card rounded-2xl shadow-ambientLg p-4 max-w-[240px]">
              <div className="text-[11px] uppercase tracking-wider font-semibold text-sapphire-700">{t.sections.locations.tag}</div>
              <div className="mt-1.5 text-[14px] font-medium text-surface-ink">Tallinn · Tartu mnt 16</div>
              <div className="text-[13px] text-surface-inkSoft">E–R 10–18</div>
            </div>
            <div className="absolute -top-3 -right-3 sm:-right-5 glass-card rounded-2xl shadow-ambientLg p-4 max-w-[240px]">
              <div className="text-[11px] uppercase tracking-wider font-semibold text-teal-500">Pärnu</div>
              <div className="mt-1.5 text-[14px] font-medium text-surface-ink">Metsa 28</div>
              <div className="text-[13px] text-surface-inkSoft">{lang === "ru" ? "Вт, Пт и Сб 9–17" : lang === "en" ? "Tue, Fri & Sat 9–17" : "T, R ja L 9–17"}</div>
            </div>
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="container-page pt-4 pb-12 lg:pt-6 lg:pb-16" data-testid="services-section">
        <div className="flex items-end justify-between gap-6 mb-10">
          <div>
            <div className="chip mb-3">{t.sections.services.tag}</div>
            <h2 className="section-title">{t.sections.services.title}</h2>
          </div>
          <Link to={pagePath("services", lang)} className="hidden md:inline-flex items-center gap-1.5 text-[14px] font-medium text-sapphire-700 hover:gap-2.5 transition-all">
            {t.sections.services.more} <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
        {loadError && (
          <div
            className="mb-6 rounded-xl border border-amber-300/60 bg-amber-50/70 px-4 py-3 text-[14px] text-amber-800 flex items-center gap-3 justify-between"
            data-testid="home-load-error"
          >
            <span>
              {lang === "et"
                ? "Sisu laadimine ebaõnnestus. Proovi uuesti."
                : lang === "ru"
                ? "Не удалось загрузить содержимое. Попробуйте ещё раз."
                : "Failed to load content. Please try again."}
            </span>
            <button
              type="button"
              onClick={retryLoad}
              className="font-medium underline underline-offset-2 hover:no-underline"
              data-testid="home-load-error-retry"
            >
              {lang === "et" ? "Proovi uuesti" : lang === "ru" ? "Повторить" : "Retry"}
            </button>
          </div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-5">
          {(clinic?.services || []).map((s, i) => {
            const Icon = ICONS[s.icon] || Stethoscope;
            const isExternal = !!s.externalUrl;
            const section = getSectionByServiceId(s.id);
            const pricingHref = section
              ? pricingSectionUrl(section.slugs.et, lang)
              : pagePath("pricing", lang);
            const linkProps = isExternal
              ? { as: "a", href: s.externalUrl, target: "_blank", rel: "noopener noreferrer" }
              : { to: pricingHref };
            const Comp = isExternal ? "a" : Link;
            return (
              <Comp
                key={s.id}
                {...(isExternal ? { href: s.externalUrl, target: "_blank", rel: "noopener noreferrer" } : { to: linkProps.to })}
                data-testid={`service-card-${s.id}`}
                className={`card-clinic group animate-fade-up ${isExternal ? "border-teal-300/60 bg-gradient-to-br from-white to-teal-200/10" : ""}`}
                style={{ animationDelay: `${i * 80}ms` }}
              >
                <div className={`h-12 w-12 rounded-xl flex items-center justify-center mb-4 transition-colors ${isExternal ? "bg-sapphire-700 text-white group-hover:bg-sapphire-600" : "bg-teal-200/50 text-teal-500 group-hover:bg-teal-200"}`}>
                  <Icon className="h-6 w-6" />
                </div>
                <h3 className="text-[17px] font-semibold text-surface-ink mb-2">{s.title[lang]}</h3>
                <p className="text-[14px] text-surface-inkSoft leading-relaxed mb-4">{s.summary[lang]}</p>
                <div className="text-[13px] font-medium text-sapphire-700 inline-flex items-center gap-1.5 group-hover:gap-2.5 transition-all">
                  {isExternal ? (
                    <>{lang === "et" ? "Ava AI Kliinik" : lang === "ru" ? "Открыть AI Клинику" : "Open AI Clinic"} <ExtLinkIcon className="h-3.5 w-3.5" /></>
                  ) : (
                    <>{t.sections.services.more} <ArrowRight className="h-3.5 w-3.5" /></>
                  )}
                </div>
              </Comp>
            );
          })}
        </div>
      </section>

      {/* HEALTH CALCULATORS — quick teaser */}
      <section className="container-page pb-16">
        <div className="rounded-3xl bg-gradient-to-br from-teal-200/40 via-white to-sapphire-50 border border-surface-outline/40 p-7 lg:p-10" data-testid="calculators-teaser">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            <div>
              <div className="chip mb-3">
                <Calculator className="h-3.5 w-3.5 inline mr-1" />
                {lang === "et" ? "TASUTA TÖÖRIISTAD" : lang === "ru" ? "БЕСПЛАТНЫЕ ИНСТРУМЕНТЫ" : "FREE TOOLS"}
              </div>
              <h2 className="section-title">
                {lang === "et"
                  ? "Tervisekalkulaatorid"
                  : lang === "ru"
                  ? "Калькуляторы здоровья"
                  : "Health calculators"}
              </h2>
              <p className="text-surface-inkSoft mt-3 text-[15px] max-w-xl">
                {lang === "et"
                  ? "HbA1c teisendus, KMI ja vööümbermõõdu risk, ICR (insuliini-süsivesikute suhe) ja ISF (insuliini tundlikkuse tegur). Tasuta ja anonüümne."
                  : lang === "ru"
                  ? "HbA1c, ИМТ и талия, ICR (соотношение инсулин/углеводы) и ISF (фактор чувствительности). Бесплатно и анонимно."
                  : "HbA1c conversion, BMI + waist risk, ICR (insulin-to-carb ratio) and ISF (insulin sensitivity factor). Free and anonymous."}
              </p>
              <Link
                to={pagePath("calculators", lang)}
                data-testid="home-calc-cta"
                className="mt-5 inline-flex items-center gap-2 rounded-lg bg-sapphire-700 hover:bg-[#005a87] text-white px-5 py-3 font-medium text-[14px] transition-colors"
              >
                {lang === "et" ? "Ava kalkulaatorid" : lang === "ru" ? "Открыть калькуляторы" : "Open calculators"}
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
            <div>
              <CalculatorCarousel lang={lang} />
            </div>
          </div>
        </div>
      </section>

      {/* DOCTORS */}
      <section className="container-page pb-16 lg:pb-24" data-testid="doctors-section">
        <div className="flex items-end justify-between gap-6 mb-10">
          <div>
            <div className="chip mb-3">{t.sections.doctors.tag}</div>
            <h2 className="section-title">{t.sections.doctors.title}</h2>
          </div>
          <Link to={pagePath("doctors", lang)} className="hidden md:inline-flex items-center gap-1.5 text-[14px] font-medium text-sapphire-700 hover:gap-2.5 transition-all">
            {t.sections.services.more} <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-5">
          {(clinic?.doctors || []).map((d, i) => (
            <div
              key={d.id}
              data-testid={`doctor-card-${d.id}`}
              className="card-clinic animate-fade-up"
              style={{ animationDelay: `${i * 80}ms` }}
            >
              <div className="aspect-[4/5] rounded-xl overflow-hidden bg-sapphire-50 mb-4">
                <img src={d.image} alt={d.name} className="w-full h-full object-cover" loading="lazy" decoding="async" width="320" height="400" />
              </div>
              <div className="text-[15px] font-semibold text-surface-ink">{d.name}</div>
              <div className="text-[13px] text-surface-inkSoft mt-0.5">{d.role[lang]}</div>
              <div className="text-[12px] text-teal-500 mt-1 font-medium uppercase tracking-wider">
                {d.location === "tallinn" ? "Tallinn" : d.location === "tallinn-parnu" ? "Tallinn & Pärnu" : "Pärnu"}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* LOCATIONS */}
      <section className="container-page pb-16 lg:pb-24">
        <div className="chip mb-3">{t.sections.locations.tag}</div>
        <h2 className="section-title mb-10">{t.sections.locations.title}</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 lg:gap-5">
          {(clinic?.locations || []).map((loc) => (
            <div key={loc.id} className="card-clinic" data-testid={`location-card-${loc.id}`}>
              <div className="flex items-start justify-between mb-4">
                <h3 className="text-2xl font-semibold text-surface-ink">{loc.city[lang]}</h3>
                <div className="h-10 w-10 rounded-xl bg-sapphire-50 flex items-center justify-center text-sapphire-700">
                  <MapPin className="h-5 w-5" />
                </div>
              </div>
              <div className="space-y-3 text-[14px]">
                <div className="flex items-start gap-2 text-surface-ink"><MapPin className="h-4 w-4 mt-0.5 text-surface-inkSoft" />{loc.address}</div>
                {loc.schedule && loc.schedule.length > 0 ? (
                  <div className="space-y-1.5 pl-1">
                    {loc.schedule.map((s, si) => (
                      <div key={si} className="flex items-start gap-2 text-[13px]" data-testid={`location-${loc.id}-schedule-${si}`}>
                        <Clock className="h-3.5 w-3.5 mt-1 text-surface-inkSoft shrink-0" />
                        <div>
                          <div className="font-medium text-surface-ink">{s.role[lang]}</div>
                          <div className="text-surface-inkSoft">{s.days[lang]}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="flex items-center gap-2 text-surface-ink"><Clock className="h-4 w-4 text-surface-inkSoft" />{loc.hours[lang]}</div>
                )}
                {loc.note && (
                  <div className="text-[12px] text-surface-inkSoft italic pl-1">{loc.note[lang]}</div>
                )}
                <div className="flex items-center gap-2 text-surface-ink"><Phone className="h-4 w-4 text-surface-inkSoft" />+372 5566 5451 · +372 5566 5135</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* BOOK CTA */}
      <section className="container-page pb-20">
        <div className="rounded-3xl bg-gradient-to-br from-sapphire-700 to-[#002757] text-white p-8 lg:p-14 relative overflow-hidden">
          <div className="absolute -right-20 -top-20 h-72 w-72 rounded-full bg-teal-200/20 blur-3xl" />
          <div className="absolute -left-10 -bottom-10 h-60 w-60 rounded-full bg-sapphire-400/20 blur-3xl" />
          <div className="relative max-w-2xl">
            <div className="text-[12px] uppercase tracking-wider font-semibold text-teal-200">{t.sections.contact.tag}</div>
            <h2 className="text-3xl lg:text-4xl font-semibold mt-2">{t.sections.contact.title}</h2>
            <p className="mt-3 text-[15px] text-white/80">{t.hero.subtitle}</p>
            <div className="mt-6 flex flex-wrap gap-3">
              <a
                href="https://veebiregistratuur.ee/#/tallinna-endokrinoloogia-kliinik/"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="cta-digireg"
                className="inline-flex items-center gap-2 rounded-lg bg-white text-sapphire-700 px-5 py-3 font-medium text-[15px] hover:bg-sapphire-50 transition-colors"
              >
                {t.footer.digital} <ExternalLink className="h-4 w-4" />
              </a>
              <Link
                to={pagePath("contact", lang)}
                data-testid="cta-email"
                className="inline-flex items-center gap-2 rounded-lg bg-white/10 backdrop-blur border border-white/20 text-white px-5 py-3 font-medium text-[15px] hover:bg-white/15 transition-colors"
              >
                {t.cta.emailUs} <ArrowRight className="h-4 w-4" />
              </Link>
              <a
                href="tel:+37255665451"
                className="inline-flex items-center gap-2 rounded-lg bg-white/10 backdrop-blur border border-white/20 text-white px-5 py-3 font-medium text-[15px] hover:bg-white/15 transition-colors"
              >
                <Phone className="h-4 w-4" /> +372 5566 5451
              </a>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
